"use client"

import { createContext, useContext, useEffect, useState } from "react"
import { useGlobalConfig } from "./global-config-context"

interface TurnstileContextValue {
	isSdkReady: boolean
	isSdkLoading: boolean
	sdkError: string | null
}

const TurnstileContext = createContext<TurnstileContextValue | undefined>(
	undefined,
)

export const useTurnstile = () => {
	const context = useContext(TurnstileContext)
	if (!context) {
		throw new Error("useTurnstile must be used within TurnstileProvider")
	}
	return context
}

export function TurnstileProvider({ children }: { children: React.ReactNode }) {
	const [isSdkReady, setIsSdkReady] = useState(false)
	const [isSdkLoading, setIsSdkLoading] = useState(true)
	const [sdkError, setSdkError] = useState<string | null>(null)
	const { globalConfig } = useGlobalConfig()

	const isTurnstileEnabled = globalConfig?.PUBLIC_TURNSTILE_ENABLED

	useEffect(() => {
		if (!isTurnstileEnabled) {
			setIsSdkLoading(false)
			setIsSdkReady(false)
			return
		}

		// If already available, mark ready
		if (window.turnstile) {
			setIsSdkLoading(false)
			setIsSdkReady(true)
			window.__cfTurnstileLoaded = true
			return
		}

		// Check for existing script tag
		const existingScript = document.getElementById(
			"cf-turnstile-sdk",
		) as HTMLScriptElement | null

		const handleLoad = () => {
			setIsSdkLoading(false)
			setIsSdkReady(true)
			setSdkError(null)
			window.__cfTurnstileLoaded = true
		}

		const handleError = () => {
			setIsSdkLoading(false)
			setIsSdkReady(false)
			setSdkError("Failed to load Turnstile SDK")
		}

		if (existingScript) {
			// If script exists but window.turnstile not ready yet, wait for it
			if (window.__cfTurnstileLoaded || window.turnstile) {
				handleLoad()
			} else {
				existingScript.addEventListener("load", handleLoad)
				existingScript.addEventListener("error", handleError)
				return () => {
					existingScript.removeEventListener("load", handleLoad)
					existingScript.removeEventListener("error", handleError)
				}
			}
			return
		}

		// Create and append the script once
		const script = document.createElement("script")
		script.id = "cf-turnstile-sdk"
		script.src =
			"https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
		script.async = true
		script.addEventListener("load", handleLoad, { once: true } as any)
		script.addEventListener("error", handleError, { once: true } as any)
		document.body.appendChild(script)

		return () => {
			script.removeEventListener("load", handleLoad)
			script.removeEventListener("error", handleError)
		}
	}, [isTurnstileEnabled])

	return (
		<TurnstileContext.Provider value={{ isSdkReady, isSdkLoading, sdkError }}>
			{children}
		</TurnstileContext.Provider>
	)
}
